import { ROUTES } from "@/constants/routes";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useSearchParams } from "react-router-dom";
import { ApiResponse } from "../api/handleApiResponse";
import { verifyEmailToken as verifyEmail } from "../api/modules";
import nariShaktiLogo from "../assets/images/Naari Shakti_logo.jpg";

const VerifyEmail = () => {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState<"pending" | "success" | "error">(
    "pending"
  );
  const navigate = useNavigate();
  const [message, setMessage] = useState("");

  useEffect(() => {
    const token = searchParams.get("token");
    if (!token) {
      setStatus("error");
      setMessage(
        t("auth.verifyEmail.invalidVerificationLink") ||
          "Invalid verification link."
      );
      return;
    }
    verifyEmail(token)
      .then((res: ApiResponse<any>) => {
        if (res.success) {
          setStatus("success");
          setMessage(
            res.message ||
              t("auth.verifyEmail.emailVerified") ||
              "Email verified successfully!"
          );
          // Auto-redirect to login after 5 seconds
          setTimeout(() => {
            navigate(ROUTES.LOGIN);
          }, 5000);
        } else {
          setStatus("error");
          setMessage(
            res.message ||
              t("auth.verifyEmail.emailVerificationFailed") ||
              "Email verification failed."
          );
        }
      })
      .catch((err: any) => {
        setStatus("error");
        setMessage(
          err?.response?.data?.message ||
            t("auth.verifyEmail.emailVerificationFailed") ||
            "Email verification failed."
        );
      });
  }, [searchParams, t, navigate]);

  return (
    <div className="min-h-screen flex items-center justify-center registration-container p-4">
      <div className="relative w-full max-w-md registration-card rounded-3xl shadow-xl p-8 border border-gray-200 flex flex-col justify-center backdrop-blur-sm">
        {/* Nari Suraksha Logo at top center, overlapping border */}
        <div className="absolute -top-12 left-1/2 -translate-x-1/2 z-20 flex items-center justify-center">
          <div
            className="w-24 h-24 rounded-full border-4 border-white bg-white flex items-center justify-center shadow-xl ring-4 ring-gray-100"
            aria-label="Nari Suraksha Logo"
          >
            <img
              src={nariShaktiLogo}
              alt={t("common.nariShaktiLogoAlt", {
                defaultValue: "Nari Suraksha Logo",
              })}
              className="w-20 h-20 object-contain rounded-full"
              loading="eager"
            />
          </div>
        </div>

        {/* Main Content */}
        <div className="flex-1 flex flex-col justify-center mt-6">
          <div className="text-center mb-8">
            <h1 className="text-3xl font-bold text-primary mb-2 tracking-tight">
              {t("auth.verifyEmail.verifyEmail") || "ईमेल सत्यापन"}
            </h1>
          </div>

          <div className="flex flex-col items-center justify-center gap-6">
            {status === "pending" && (
              <div className="flex flex-col items-center gap-4">
                <div className="w-16 h-16 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
                <p className="text-gray-600 text-lg font-medium">
                  {t("auth.verifyEmail.verifying") || "सत्यापित हो रहा है..."}
                </p>
                <p className="text-sm text-gray-500 text-center max-w-xs">
                  कृपया प्रतीक्षा करें जबकि हम आपके ईमेल को सत्यापित करते हैं
                </p>
              </div>
            )}

            {status === "success" && (
              <div className="flex flex-col items-center gap-6">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center">
                  <svg
                    className="w-8 h-8 text-green-600"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path
                      fillRule="evenodd"
                      d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                      clipRule="evenodd"
                    />
                  </svg>
                </div>
                <div className="text-center">
                  <p className="text-green-600 font-semibold text-lg mb-2">
                    {message}
                  </p>
                  <p className="text-sm text-gray-500">
                    आपका ईमेल सफलतापूर्वक सत्यापित हो गया है!
                  </p>
                </div>
                <button
                  className="w-full px-8 py-4 rounded-2xl font-semibold bg-primary text-white hover:bg-pink-700 transition-all duration-300 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 border-2 border-primary"
                  onClick={() => navigate(ROUTES.LOGIN)}
                >
                  <div className="flex items-center justify-center gap-2">
                    {t("auth.verifyEmail.goToLogin") || "लॉगिन पर जाएं"}
                    <svg
                      className="w-4 h-4"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M9 5l7 7-7 7"
                      />
                    </svg>
                  </div>
                </button>
                <div className="text-xs text-gray-500 text-center bg-gray-50 px-4 py-2 rounded-xl border border-gray-200">
                  {t("auth.verifyEmail.redirectingToLogin") ||
                    "5 सेकंड में लॉगिन पर पुनः निर्देशित हो रहे हैं..."}
                </div>
              </div>
            )}

            {status === "error" && (
              <div className="flex flex-col items-center gap-6">
                <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center">
                  <svg
                    className="w-8 h-8 text-red-600"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path
                      fillRule="evenodd"
                      d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
                      clipRule="evenodd"
                    />
                  </svg>
                </div>
                <div className="text-center">
                  <p className="text-red-600 font-semibold text-lg mb-2">
                    {message}
                  </p>
                  <p className="text-sm text-gray-500">
                    कृपया पुनः प्रयास करें या समर्थन से संपर्क करें
                  </p>
                </div>
                <button
                  className="w-full px-8 py-4 rounded-2xl font-semibold bg-primary text-white hover:bg-pink-700 transition-all duration-300 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 border-2 border-primary"
                  onClick={() => navigate(ROUTES.LOGIN)}
                >
                  <div className="flex items-center justify-center gap-2">
                    लॉगिन पर वापस जाएं
                    <svg
                      className="w-4 h-4"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M9 5l7 7-7 7"
                      />
                    </svg>
                  </div>
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default VerifyEmail;
